import { createAudioPlayer, setAudioModeAsync } from 'expo-audio';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, TouchableOpacity } from 'react-native';
import { ProgressBar } from 'react-native-paper';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Document, LocalFile } from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import theme from '@themes/theme';
import { AsyncStorage } from '@utils/storage';
import { formatToMMSS } from '@utils/voiceUtils';

import type { AudioPlayer, AudioStatus } from 'expo-audio';

interface VoiceCardInterface {
  voice: Document | LocalFile;
  onDelete?: (voice: LocalFile) => void;
  onLongPress?: () => void;
}
export const VoiceCard: React.FC<VoiceCardInterface> = ({
  voice,
  onDelete,
  onLongPress,
}) => {
  const playerRef = useRef<AudioPlayer | null>(null);

  useEffect(() => {
    setAudioModeAsync({ playsInSilentMode: true }).catch(() => {});
  }, []);

  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [pausedPosition, setPausedPosition] = useState<number>(0);
  const [recordTime, setRecordTime] = useState(0);
  const { playingVoiceMemoId, setPlayingVoiceMemoId } = useChatInput();
  const [playingCurrentVoiceFile, setPlayingCurrentVoiceFile] = useState(false);
  const { checkDocumentURL } = useGetDocumentsByClientIdsQuery();

  const [uri, setUri] = useState<string>('');

  const voiceAssetIsLocal = (
    voiceAsset: Document | LocalFile
  ): voiceAsset is LocalFile => {
    return (
      (voiceAsset as LocalFile)?.url?.startsWith('file:') ||
      (voiceAsset as LocalFile)?.url?.startsWith('data:')
    );
  };

  const setUriFromCache = async () => {
    const url = await AsyncStorage.getItem(voice.clientId);
    if (!url) {
      await checkDocumentURL([voice as Document]);
      setTimeout(async () => {
        await setUriFromCache();
      }, 1000);
      return;
    }
    setUri(url);
  };
  useEffect(() => {
    if (voiceAssetIsLocal(voice)) {
      setUri(voice.url);
    } else {
      setUriFromCache();
    }
  }, [voice.clientId]);

  const isSending = uri?.startsWith('file:') && !voiceAssetIsLocal(voice);

  const handleStatusUpdate = (status: AudioStatus) => {
    if (!status.isLoaded) return;
    setLoading(false);

    const { currentTime, duration, playing } = status;

    if (currentTime === undefined || duration === undefined || duration === 0)
      return;

    const remainingSeconds = Math.floor(duration - currentTime);
    setPausedPosition(currentTime);
    setRecordTime(remainingSeconds);
    setProgress(currentTime / duration);

    if (!playing && currentTime >= duration) {
      setPlayingVoiceMemoId('');
      setPausedPosition(0);
      setProgress(1);
    }
  };

  const playRecording = async () => {
    if (!uri) return;
    setLoading(true);
    setPlayingVoiceMemoId(voice.id);

    // Clean up previous player
    if (playerRef.current) {
      playerRef.current.release();
    }

    const player = createAudioPlayer({ uri });
    playerRef.current = player;

    player.addListener('playbackStatusUpdate', handleStatusUpdate);

    if (progress !== 1 && pausedPosition > 0) {
      await player.seekTo(pausedPosition);
    }

    player.play();
  };

  const pausePlaying = async () => {
    playerRef.current?.pause();
    setPlayingVoiceMemoId('');
  };

  useEffect(() => {
    return () => {
      setPlayingVoiceMemoId('');
      playerRef.current?.release();
    };
  }, []);

  useEffect(() => {
    setPlayingCurrentVoiceFile(playingVoiceMemoId === voice.id);
    // a different voice file started playing — release this player
    if (
      playingVoiceMemoId &&
      playingVoiceMemoId !== voice.id &&
      playerRef.current
    ) {
      playerRef.current.release();
      playerRef.current = null;
    }
  }, [playingVoiceMemoId]);

  return (
    <Box
      flexDirection='row'
      alignItems='center'
      borderColor='grey02'
      borderWidth={1}
      borderRadius='xs'
      padding='xs'
      backgroundColor={loading ? 'grey01' : 'white'}
      opacity={isSending ? 0.3 : 1}
      minWidth={Platform.OS === 'web' ? 320 : 220}>
      <TouchableOpacity
        onLongPress={onLongPress}
        onPress={() => {
          if (loading || !uri) return;
          playingCurrentVoiceFile ? pausePlaying() : playRecording();
        }}
        hitSlop={{ top: 10, bottom: 10, right: 10, left: 10 }}>
        <Icon
          name={playingCurrentVoiceFile ? 'PauseCircle' : 'PlayCircle'}
          variant='l'
          color={loading || !uri ? 'grey03' : 'black'}
        />
      </TouchableOpacity>
      <Box flex={1} marginHorizontal='xs'>
        <TouchableOpacity onLongPress={onLongPress}>
          <ProgressBar
            style={{
              height: 4,
              backgroundColor: theme.colors.grey05,
            }}
            progress={progress}
            color={theme.colors.grey03}
          />
        </TouchableOpacity>
      </Box>
      <TouchableOpacity onLongPress={onLongPress}>
        <Text
          variant='metadata'
          color='grey05'
          textAlign='center'
          // set a width so the recordTime changing will not affect the progress bar flex
          style={{ width: 42 }}>
          {(playingCurrentVoiceFile || pausedPosition) && progress !== 1
            ? formatToMMSS(recordTime || 0)
            : formatToMMSS(voice?.duration || 0)}
        </Text>
      </TouchableOpacity>
      {onDelete && (
        <TouchableOpacity onPress={() => onDelete(voice as LocalFile)}>
          <Icon name='X2' variant='l' marginLeft='xs' color='textPrimary' />
        </TouchableOpacity>
      )}
    </Box>
  );
};
